import { isSioHost } from '@systemeio/util'
import { settingsMenuHref } from 'modules/profile/constants/settings-menu-href'
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys'
import { SettingsMenuEnum } from 'modules/profile/enums/settings-menu-enum'
import { SettingsMenuInterface } from 'modules/profile/types/settings-interface'
import { getMenuItemDataTestId } from 'modules/profile/utils/get-menu-item-data-test-id'
import { useRouter } from 'next/router'
import { AdminRolesEnum } from 'shared/enums/user-roles-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'

export const useSettingsMenuItems = (): SettingsMenuInterface => {
  const { t } = useLocoTranslation()
  const {
    isWorkspaceOwner,
    isWsAdmin,
    isCustomer,
    isAffiliate,
    isStudent,
    checkImpersonatorAdminRoles,
    isSupportImpersonation,
    isSubAccountImpersonation,
  } = useUser()

  const { pathname } = useRouter()

  const regularIsActiveCheck = (url: string) => pathname.includes(url)

  const menuItems: SettingsMenuInterface = [
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.profile]),
      href: settingsMenuHref[SettingsMenuEnum.profile],
      shouldRender: (isCustomer || isAffiliate) && isWorkspaceOwner,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.profile),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.account]),
      href: settingsMenuHref[SettingsMenuEnum.account],
      shouldRender: isCustomer || isStudent,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.account),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.my_plan]),
      href: settingsMenuHref[SettingsMenuEnum.my_plan],
      shouldRender: isCustomer && isWorkspaceOwner,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.my_plan),
      isActive: regularIsActiveCheck,
    },

    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.payment_gateways]),
      href: settingsMenuHref[SettingsMenuEnum.payment_gateways],
      shouldRender: isCustomer && isWorkspaceOwner,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.payment_gateways),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.manage_subscriptions]),
      href: settingsMenuHref[SettingsMenuEnum.manage_subscriptions],
      shouldRender: (isCustomer || isStudent) && isWorkspaceOwner,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.manage_subscriptions),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.mailing]),
      href: settingsMenuHref[SettingsMenuEnum.mailing],
      shouldRender: isCustomer,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.mailing),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.membership]),
      href: settingsMenuHref[SettingsMenuEnum.membership],
      shouldRender: isCustomer,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.membership),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.payment_page]),
      href: settingsMenuHref[SettingsMenuEnum.payment_page],
      shouldRender: isCustomer && (isWorkspaceOwner || isWsAdmin),
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.payment_page),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.sales_funnel]),
      href: settingsMenuHref[SettingsMenuEnum.sales_funnel],
      shouldRender: isCustomer,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.sales_funnel),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.shipping]),
      href: settingsMenuHref[SettingsMenuEnum.shipping],
      shouldRender: isCustomer && isWorkspaceOwner,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.shipping),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.affiliate_program]),
      href: settingsMenuHref[SettingsMenuEnum.affiliate_program],
      shouldRender: isCustomer && isWorkspaceOwner && isSioHost(window.location.host),
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.affiliate_program),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.custom_domains]),
      href: settingsMenuHref[SettingsMenuEnum.custom_domains],
      shouldRender: isCustomer && isWorkspaceOwner,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.custom_domains),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.public_api]),
      href: settingsMenuHref[SettingsMenuEnum.public_api],
      shouldRender:
        isCustomer && (isWorkspaceOwner || isWsAdmin) && isSioHost(window.location.host),
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.public_api),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.webhooks]),
      href: settingsMenuHref[SettingsMenuEnum.webhooks],
      shouldRender:
        isCustomer && (isWorkspaceOwner || isWsAdmin) && isSioHost(window.location.host),
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.webhooks),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.integrations]),
      href: settingsMenuHref[SettingsMenuEnum.integrations],
      shouldRender: isCustomer && (isWorkspaceOwner || isWsAdmin),
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.integrations),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.affiliate_commissions]),
      href: settingsMenuHref[SettingsMenuEnum.affiliate_commissions],
      shouldRender:
        (isCustomer || isAffiliate) && isWorkspaceOwner && isSioHost(window.location.host),
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.affiliate_commissions),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.notification]),
      href: settingsMenuHref[SettingsMenuEnum.notification],
      shouldRender: true,
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.notification),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.workspace_members]),
      href: settingsMenuHref[SettingsMenuEnum.workspace_members],
      shouldRender:
        isCustomer && (isWorkspaceOwner || isWsAdmin) && isSioHost(window.location.host),
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.workspace_members),
      isActive: regularIsActiveCheck,
    },
    {
      name: t(settingsMenuLocoKeys[SettingsMenuEnum.change_password]),
      href: settingsMenuHref[SettingsMenuEnum.change_password],
      shouldRender: (() => {
        if (isSubAccountImpersonation) return false
        if (isSupportImpersonation)
          return checkImpersonatorAdminRoles([AdminRolesEnum.admin, AdminRolesEnum.superAdmin])
        return isWorkspaceOwner
      })(),
      dataTestId: getMenuItemDataTestId(SettingsMenuEnum.change_password),
      isActive: regularIsActiveCheck,
    },
  ]

  return menuItems
}
